import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@headlessui/react";
import { FaBookOpen, FaCheck, FaEye, FaSearch, FaTable, FaThLarge, FaTimes, FaUserGraduate } from "react-icons/fa";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";

const buildCsvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const AdminAdmissions = () => {
  const { isDarkMode } = useAppTheme();
  const [admissions, setAdmissions] = useState([]);
  const [search, setSearch] = useState("");
  const [filterClass, setFilterClass] = useState("All");
  const [selected, setSelected] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("card");
  const [activeView, setActiveView] = useState("directory");

  useEffect(() => {
    const fetchAdmissions = async () => {
      try {
        const response = await api.get(API_PATHS.admission.adminDashboard);
        const next = Array.isArray(response.data) ? response.data : [];
        setAdmissions(next);
      } catch (error) {
        console.error("Error fetching admissions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdmissions();
  }, []);

  const filteredAdmissions = useMemo(() => admissions.filter((admission) => {
    const firstName = String(admission.firstName || "").toLowerCase();
    const lastName = String(admission.lastName || "").toLowerCase();
    const nameMatch = firstName.includes(search.toLowerCase()) || lastName.includes(search.toLowerCase());
    const classMatch = filterClass === "All" || admission.standard === filterClass;
    return nameMatch && classMatch;
  }), [admissions, filterClass, search]);

  const standards = useMemo(
    () => [...new Set(admissions.map((a) => a.standard).filter(Boolean))],
    [admissions]
  );

  const openModal = (admission) => {
    setSelected(admission);
    setIsOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeModal = () => setIsOpen(false);

  const toggleView = () => {
    setViewMode((prev) => (prev === "card" ? "table" : "card"));
  };

  const handleDecision = (admissionId, decision) => {
    toast.info(`${decision} action for admission ${admissionId?.slice(-6) || "record"} is not connected yet.`);
  };

  const exportToExcel = () => {
    const headers = ["First Name", "Last Name", "Standard", "Email", "Contact", "Amount", "Address", "Date"];
    const rows = filteredAdmissions.map((admission) => [
      admission.firstName,
      admission.lastName,
      admission.standard,
      admission.email,
      admission.phone || admission.contactNumber || "",
      admission.amount,
      admission.address,
      admission.date ? new Date(admission.date).toLocaleDateString() : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(buildCsvValue).join(","))
      .join("\n");

    const data = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(data, "admissions.csv");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ["Name", "Standard", "Amount", "Address", "Date"];
    const tableRows = filteredAdmissions.map((admission) => [
      `${admission.firstName || ""} ${admission.lastName || ""}`.trim(),
      admission.standard || "",
      admission.amount || "",
      admission.address || "",
      admission.date ? new Date(admission.date).toLocaleDateString() : "",
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
    });
    doc.save("admissions.pdf");
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-xl text-indigo-600">
        Loading Admissions...
      </div>
    );
  }

  return (
    <div>
      <AdminResponsiveTabs tabs={[{ key: "overview", label: "Overview" }, { key: "directory", label: "Directory" }]} activeKey={activeView} onChange={setActiveView} tone="indigo" isDarkMode={isDarkMode} />

      <div className={`border-x border-b p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
        {activeView === "overview" && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Total applications", value: admissions.length },
                { label: "Filtered results", value: filteredAdmissions.length },
                { label: "Standards covered", value: standards.length },
                { label: "Search active", value: search || filterClass !== "All" ? "Yes" : "No" },
              ].map((item) => (
                <div key={item.label} className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
                  <p className="mt-2 text-2xl font-bold">{item.value}</p>
                </div>
              ))}
            </div>

            <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Standard</th>
                      <th className="px-4 py-3 text-left font-semibold">Applications</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    {standards.map((std) => (
                      <tr key={std} className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                        <td className="px-4 py-3">{std}</td>
                        <td className="px-4 py-3">{admissions.filter((a) => a.standard === std).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeView === "directory" && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3 md:justify-between">
              <div className="flex flex-wrap gap-2">
                <button onClick={exportToExcel} className="rounded-full bg-emerald-100 px-3 py-2 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-200">Export CSV</button>
                <button onClick={exportToPDF} className="rounded-full bg-red-100 px-3 py-2 text-sm font-semibold text-red-800 transition hover:bg-red-200">Export PDF</button>
                <button onClick={toggleView} className="flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-200">
                  {viewMode === "card" ? <FaTable /> : <FaThLarge />}
                  {viewMode === "card" ? "Table view" : "Card view"}
                </button>
              </div>

              <div className="flex flex-col gap-2 md:flex-row">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by student name"
                    className={`w-full rounded-2xl border py-2.5 pl-10 pr-3 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500" : "border-gray-300"}`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  className={`rounded-2xl border p-2.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300"}`}
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                >
                  <option value="All">All Standards</option>
                  {standards.map((std) => (
                    <option key={std} value={std}>{std}</option>
                  ))}
                </select>
              </div>
            </div>

            {filteredAdmissions.length === 0 ? (
              <p className={`py-10 text-center ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>No admissions found.</p>
            ) : viewMode === "card" ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                {filteredAdmissions.map((admission) => (
                  <div key={admission._id} className={`rounded-2xl border p-4 shadow-lg transition-all duration-300 hover:shadow-xl sm:p-6 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-gray-200 bg-white"}`}>
                    <div className="mb-4 flex items-center">
                      <div className="mr-4 rounded-full bg-indigo-100 p-3 text-indigo-600">
                        <FaUserGraduate className="text-xl" />
                      </div>
                      <div>
                        <h2 className={`text-lg font-semibold ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}>
                          {admission.firstName} {admission.lastName}
                        </h2>
                        <p className="text-sm text-gray-500">ID: {admission._id?.slice(-6)}</p>
                      </div>
                    </div>

                    <div className={`mb-4 flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-gray-600"}`}>
                      <FaBookOpen className="text-indigo-400" />
                      <span>Standard: {admission.standard}</span>
                    </div>

                    <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
                      <button onClick={() => openModal(admission)} className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-100 px-4 py-2 font-medium text-blue-700 transition hover:bg-blue-200 sm:w-auto"><FaEye />View</button>
                      <button onClick={() => handleDecision(admission._id, "Approve")} className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-100 px-4 py-2 font-medium text-green-700 transition hover:bg-green-200 sm:w-auto"><FaCheck />Approve</button>
                      <button onClick={() => handleDecision(admission._id, "Reject")} className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-100 px-4 py-2 font-medium text-red-700 transition hover:bg-red-200 sm:w-auto"><FaTimes />Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`overflow-x-auto rounded-2xl shadow-md ${isDarkMode ? "bg-slate-950" : "bg-white"}`}>
                <table className="w-full min-w-[640px] table-auto text-left text-xs sm:text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-200" : "bg-indigo-100 text-indigo-700"}>
                    <tr>
                      <th className="px-3 py-3 sm:px-4">ID</th>
                      <th className="px-3 py-3 sm:px-4">Name</th>
                      <th className="px-3 py-3 sm:px-4">Standard</th>
                      <th className="hidden px-3 py-3 md:table-cell sm:px-4">Email</th>
                      <th className="hidden px-3 py-3 lg:table-cell sm:px-4">Phone</th>
                      <th className="px-3 py-3 sm:px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdmissions.map((admission) => (
                      <tr key={admission._id} className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                        <td className="px-3 py-2 sm:px-4">{admission._id?.slice(-6)}</td>
                        <td className="px-3 py-2 sm:px-4">{admission.firstName} {admission.lastName}</td>
                        <td className="px-3 py-2 sm:px-4">{admission.standard}</td>
                        <td className="hidden px-3 py-2 md:table-cell sm:px-4">{admission.email}</td>
                        <td className="hidden px-3 py-2 lg:table-cell sm:px-4">{admission.phone || admission.contactNumber || "-"}</td>
                        <td className="px-3 py-2 sm:px-4">
                          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                            <button onClick={() => openModal(admission)} className="rounded bg-blue-500 px-3 py-1 text-white hover:bg-blue-600">View</button>
                            <button onClick={() => handleDecision(admission._id, "Approve")} className="rounded bg-green-500 px-3 py-1 text-white hover:bg-green-600">Approve</button>
                            <button onClick={() => handleDecision(admission._id, "Reject")} className="rounded bg-red-500 px-3 py-1 text-white hover:bg-red-600">Reject</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={isOpen} onClose={closeModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className={`w-full max-w-md rounded-xl p-6 shadow-xl ${isDarkMode ? "bg-slate-900 text-slate-100" : "bg-white text-slate-800"}`}>
            <Dialog.Title className="mb-4 text-center text-lg font-bold">Admission Details</Dialog.Title>
            {selected && (
              <div className="space-y-2">
                <p><strong>Name:</strong> {selected.firstName} {selected.lastName}</p>
                <p><strong>Standard:</strong> {selected.standard}</p>
                <p><strong>Phone:</strong> {selected.phone || selected.contactNumber || "-"}</p>
                <p><strong>Email:</strong> {selected.email || "-"}</p>
                <p><strong>Address:</strong> {selected.address || "-"}</p>
                <p><strong>Amount Paid:</strong> ₹{selected.amount || 0}</p>
                <p><strong>Date:</strong> {selected.date ? new Date(selected.date).toLocaleDateString() : "-"}</p>
              </div>
            )}
            <div className="mt-4 text-center">
              <button onClick={closeModal} className="rounded bg-indigo-500 px-4 py-2 text-white hover:bg-indigo-600">Close</button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default AdminAdmissions;
